import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { StripeBadgeComponent } from '../../../shared/components/stripe-badge/stripe-badge.component';
import { RevealDirective } from '../../../shared/directives/reveal.directive';
import { SecureTradeExplainerComponent } from './secure-trade-explainer.component';

/**
 * Public marketing page explaining Galopa's protected-payment trading flow
 * for saddle deals struck off-platform (Facebook Marketplace, a saddlery
 * noticeboard, etc.) — same shared backend as the on-platform buy-now/offer
 * flow (see the escrow plan), but framed entirely around saddle trading and
 * deliberately never using the word "escrow" anywhere user-facing.
 */
@Component({
  selector: 'app-secure-trade-landing',
  standalone: true,
  imports: [
    RouterLink,
    IconComponent,
    StripeBadgeComponent,
    RevealDirective,
    SecureTradeExplainerComponent,
  ],
  templateUrl: './secure-trade-landing.component.html',
  styleUrl: './secure-trade-landing.component.css',
})
export class SecureTradeLandingComponent {
  scrollToHowItWorks(event: Event): void {
    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById('how-it-works')?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  readonly steps = [
    {
      n: '1',
      title: 'Agree on the deal',
      body: 'Start a trade with the saddle, price and any conditions. We invite the other person by email — no account needed until they accept.',
      img: 'assets/marketing/secure-trade-agree.webp',
      alt: 'A rider outdoors on her phone, agreeing a remote saddle deal',
    },
    {
      n: '2',
      title: 'Buyer pays in',
      body: "The buyer's card is charged and the funds sit safely with Galopa — not with the seller, not yet.",
      img: 'assets/marketing/secure-trade-held.webp',
      alt: 'A leather saddle beside a brass lock and key',
    },
    {
      n: '3',
      title: 'Seller ships it',
      body: 'Knowing the money is real, the seller posts the saddle anywhere in Australia and adds courier + tracking.',
      img: 'assets/marketing/secure-trade-pack.webp',
      alt: 'Hands packing a leather saddle for shipping',
    },
    {
      n: '4',
      title: 'Buyer approves, funds release',
      body: "Check the saddle matches what was described, then release the payment. Something's wrong? Flag it instead — nothing releases automatically.",
      img: 'assets/marketing/secure-trade-inspect.webp',
      alt: 'A rider inspecting a leather saddle in a tack room',
    },
  ];

  readonly cities: { name: string; x: number; y: number; anchor: 'start' | 'middle' | 'end' }[] = [
    { name: 'Darwin', x: 177.7, y: 46.0, anchor: 'middle' },
    { name: 'Perth', x: 52.0, y: 235.1, anchor: 'end' },
    { name: 'Alice', x: 203.2, y: 155.0, anchor: 'start' },
    { name: 'Brisbane', x: 363.9, y: 191.6, anchor: 'end' },
    { name: 'Adelaide', x: 242.8, y: 264.0, anchor: 'end' },
    { name: 'Sydney', x: 348.6, y: 253.7, anchor: 'end' },
    { name: 'Melbourne', x: 296.2, y: 291.9, anchor: 'end' },
    { name: 'Hobart', x: 316.0, y: 341.1, anchor: 'middle' },
  ];

  /** Real coastline (Natural Earth / world.geo.json), equirectangular. */
  readonly australiaMainlandPath =
    'M284.4 58.6L287.4 66.2L292.8 62.6L295.6 66.7L299.6 70.5L298.8 74.8L300.6 83.1L301.8 87.9L303.9 89.1L306.2 97.4L305.4 102.5L308.1 109.0L317.2 114.1L323.1 118.7L328.8 122.9L327.7 125.3L332.5 131.4L335.7 141.9L339.1 139.7L342.5 143.9L344.5 142.5L346.0 152.7L351.9 158.7L355.8 162.4L362.4 170.3L364.8 178.0L365.0 183.6L364.4 189.6L368.4 197.8L367.9 206.4L366.5 210.9L364.2 219.6L364.4 225.1L362.7 232.1L359.0 240.9L352.8 245.7L349.7 253.2L346.9 258.0L344.4 266.4L341.2 271.2L339.1 278.5L338.0 285.1L338.4 288.2L333.6 291.6L324.2 291.9L316.5 295.9L312.6 299.7L307.5 303.8L300.6 299.5L295.5 297.8L296.8 292.8L292.2 294.6L284.8 301.6L277.6 299.0L272.8 297.5L268.0 296.8L259.9 294.0L254.5 288.0L252.9 280.6L251.0 275.7L246.8 271.8L238.8 270.6L241.5 265.9L239.5 258.7L235.4 265.4L227.9 267.2L232.3 261.8L233.6 256.2L236.8 251.5L236.2 244.3L229.3 252.6L224.1 255.9L220.9 263.6L214.3 259.6L214.6 254.5L209.3 247.4L204.9 243.8L206.5 241.6L195.7 235.7L189.8 235.4L181.8 230.7L166.7 231.6L155.9 235.1L146.3 238.3L138.3 237.7L129.4 242.6L122.2 244.9L120.5 250.0L117.4 253.9L110.3 254.1L105.0 255.0L97.6 253.2L91.6 254.3L85.8 254.7L80.8 259.9L78.4 259.5L74.2 262.2L70.2 265.3L64.0 264.9L58.4 264.9L49.5 258.7L45.0 256.9L45.2 251.3L49.4 250.0L50.8 247.8L50.5 244.3L51.5 237.6L50.6 231.8L46.1 222.0L44.8 216.5L45.1 210.9L41.8 204.6L41.6 201.8L37.8 197.9L36.8 190.3L32.0 182.6L30.8 178.5L34.5 182.7L31.7 173.7L35.9 176.5L38.3 180.3L38.2 175.3L34.1 167.6L33.2 164.6L31.3 161.7L32.2 156.1L33.9 153.7L35.1 148.8L34.2 143.2L37.6 136.2L38.3 143.6L41.8 136.9L48.6 133.6L52.7 129.5L59.1 126.0L63.0 125.2L65.3 126.4L71.9 122.8L77.0 121.7L78.2 119.6L80.5 118.7L85.1 118.9L93.9 116.1L98.5 111.8L100.6 106.6L105.5 101.7L105.9 97.8L106.1 92.5L112.0 84.3L115.5 92.6L119.1 90.7L116.1 86.1L118.8 81.4L122.5 83.5L123.5 76.1L128.1 71.4L130.1 67.5L134.3 65.9L134.4 63.2L138.1 64.3L138.3 61.9L142.0 60.5L146.0 59.2L152.2 63.6L156.9 69.4L162.1 69.4L167.5 70.3L165.7 65.0L169.7 57.2L173.5 54.7L172.2 52.3L175.8 46.7L180.9 43.3L185.2 44.5L192.2 42.6L192.1 37.7L185.9 34.5L190.4 33.1L196.0 35.5L200.4 39.5L207.5 41.9L209.9 41.0L215.1 43.9L220.0 41.2L223.1 42.0L225.1 40.1L229.0 44.9L226.7 50.1L223.5 54.1L220.7 54.4L221.6 58.3L219.2 63.1L216.2 67.9L216.8 70.6L223.5 76.0L229.9 79.1L234.2 82.4L240.3 88.2L242.7 88.2L247.1 90.6L248.3 93.6L256.3 96.9L261.9 93.6L263.5 88.4L265.2 84.1L266.3 78.8L268.8 71.1L267.7 66.4L268.3 63.6L267.3 58.0L268.4 50.7L270.0 48.7L268.7 45.5L270.7 40.3L272.3 35.0L272.5 32.2L275.6 28.6L278.0 33.3L278.6 39.4L280.7 40.6L281.0 44.7L284.1 49.6L284.7 55.1L284.4 58.6Z';

  readonly australiaTasmaniaPath =
    'M299.8 320.9L307.9 324.2L312.5 322.9L319.1 321.0L324.1 321.7L324.7 333.2L321.8 336.5L320.9 344.3L318.0 341.7L312.2 348.5L310.4 347.9L305.3 347.6L300.1 339.3L299.0 332.9L294.1 324.5L294.3 320.0L299.8 320.9Z';
}
